import { computed, ref, Ref } from 'vue';
import { useRouter } from 'vue-router';
import ApiService from '@/core/services/ApiService';
import {
  ISection,
  IResources,
  ResourceKind,
  resourceHref,
  resourceKind,
} from '../view/learningPath';

export interface INavEntry {
  section: ISection
  sectionIndex: number
  resource: IResources
  // posição do recurso dentro da seção (0-based)
  index: number
}

// Navegação entre os itens de uma trilha a partir de /pathways/{id}/sections
export const usePathwayItemNav = (pathwayId: Ref<string>, kind: ResourceKind, resourceId: Ref<string | number>) => {
  const router = useRouter();
  const sections = ref<ISection[]>([]);

  const fetchSections = async () => {
    try {
      const { data } = await ApiService.get(`/pathways/${pathwayId.value}/sections`);
      sections.value = (data.data || []).filter((section: ISection) => String(section.pathway_id) === String(pathwayId.value));
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const entries = computed<INavEntry[]>(() =>
    sections.value.flatMap((section, sectionIndex) =>
      section.resources.map((resource, index) => ({ section, sectionIndex, resource, index }))
    )
  );

  const currentIndex = computed(() =>
    entries.value.findIndex(({ resource }) =>
      resourceKind(resource) === kind && String(resource.resource_id ?? resource.resource?.id) === String(resourceId.value)
    )
  );

  const current = computed(() => entries.value[currentIndex.value] ?? null);
  const prev = computed(() => currentIndex.value > 0 ? entries.value[currentIndex.value - 1] : null);
  const next = computed(() =>
    currentIndex.value >= 0 && currentIndex.value < entries.value.length - 1 ? entries.value[currentIndex.value + 1] : null
  );

  const pathwayHref = computed(() => `/training/learning-paths/${pathwayId.value}`);

  const hrefOf = (entry: INavEntry | null) =>
    (entry && resourceHref(pathwayId.value, entry.resource)) || pathwayHref.value;

  // Sem próximo item, volta para a página da trilha
  const goNext = () => router.push(hrefOf(next.value));

  return {
    sections,
    fetchSections,
    current,
    prev,
    next,
    pathwayHref,
    hrefOf,
    goNext,
  };
};
